import React, { useCallback } from "react";
import {
  AccountActionCreators,
  MenuActionCreators
} from "../../redux/action-creators";
import { AccountSelectors, UserContextSelectors } from "../../redux/selectors";
import {
  CashierTab,
  Channel,
  CustomerRelationsType,
  DialogType,
  PersonalLimitsPopupType,
  Topic
} from "../../constants";
import {
  MessageBrokerUtils,
  PageContextUtils,
  TrackingUtils
} from "../../utils";
import {
  useAccountCommunicationHandlers,
  useNativeCommunicationHandlers
} from "../../hooks";
import { useDispatch, useSelector } from "react-redux";
import { Brand } from "../../configurations";
import { EmptyComponent } from "../../components/common/EmptyComponent";
import { FormattedMessage } from "react-intl";
import Loadable from "react-loadable";
import PersonalLimitsApi from "../../redux/api/personalLimitsApi";
import RegulationLogosSection from "../../segmentedComponents/RegulationLogosSection";
import { useMiniCashierCommunicationHandler } from "../../components/MiniCashier";
import { LocalizationMessages } from "../../localization/messages";
import { performAction } from "../../utils/actionUtils";
import { DrawerActionType } from "../../components/ControlCenter";
import { TrackingInfo as trackingInfo } from "../../../src/components/tracking/TrackingInfoUserAreaDrawer";
import { useLogout } from "../../hooks/useLogout";

const getLayout = brandId => {
  if (!Number.isInteger(brandId)) return null;

  const { name } = PageContextUtils.getClientProviderData();

  return Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "[request]" */
        `../Brands/${brandId}/${name}/UsefulLinks.layout`
      ),
    loading: EmptyComponent
  });
};

export const useCommunicationHandlers = () => {
  const dispatch = useDispatch();
  const rms = useSelector(UserContextSelectors.RMS);
  const isAuthenticated = useSelector(UserContextSelectors.isAuthenticated);
  const drawerActive = useSelector(AccountSelectors.drawerActive);

  const handleCustomerRelationsClick = useCallback(
    (target, customerRelationsType) => {
      const data = {
        target: target,
        featureType: customerRelationsType,
        playerValue: rms,
        isAuthenticated: isAuthenticated
      };

      MessageBrokerUtils.publish({
        channel: Channel.CUSTOMER_RELATIONS,
        topic: Topic.CTA_CustomerRelations_Open,
        headers: {
          publisher: "unifiedclient"
        },
        data
      });
    },
    [isAuthenticated, rms]
  );

  const handleChatClick = useCallback(() => {
    dispatch(MenuActionCreators.hideHamburgerMenu());
    if (drawerActive) {
      dispatch(AccountActionCreators.toggleAccountDrawer());
    }
    performAction("openChat", {
      launchInfo: { channel: { area: "Menu", element: "Menu" } }
    });
  }, [dispatch, drawerActive]);

  const handleOnlineHelp = useCallback(() => {
    handleCustomerRelationsClick(
      "OnlineHelpTab",
      CustomerRelationsType.OnlineHelp
    );
  }, [handleCustomerRelationsClick]);

  const handleHelpCenter = useCallback(() => {
    dispatch(MenuActionCreators.hideHamburgerMenu());
    performAction("openHelp");
  }, [dispatch]);

  const handleContactUs = useCallback(() => {
    handleCustomerRelationsClick("SendEmailTab", CustomerRelationsType.Email);
  }, [handleCustomerRelationsClick]);

  return {
    handleChatClick,
    handleOnlineHelp,
    handleContactUs,
    handleHelpCenter
  };
};

const UsefulLinks = () => {
  const isMobile = useSelector(UserContextSelectors.isMobile);
  const brandId = useSelector(UserContextSelectors.brandId);
  const cid = useSelector(UserContextSelectors.cid);
  const drawerActive = useSelector(AccountSelectors.drawerActive);
  const dispatch = useDispatch();

  const {
    handleChatClick,
    handleOnlineHelp,
    handleHelpCenter,
    handleContactUs
  } = useCommunicationHandlers();

  const { openCashier } = useMiniCashierCommunicationHandler();
  const { openMyAccount } = useAccountCommunicationHandlers();
  const { openBiometricSettings } = useNativeCommunicationHandlers();
  const { showLogoutPopup } = useLogout();

  const handleLogout = async () => {
    if (brandId === Brand.Sport_NL) {
      let options = {};
      const { response } = await PersonalLimitsApi.personalLimits();
      if (
        response &&
        !!response.DisplayableLimits &&
        response.DisplayableLimits.length > 0
      ) {
        options = {
          displayableLimits: response.DisplayableLimits,
          type: PersonalLimitsPopupType.Popup
        };
      }
      MessageBrokerUtils.publish({
        channel: Channel.UI,
        topic: Topic.Dialog,
        headers: {
          publisher: "UAA"
        },
        data: {
          dialogType: DialogType.PersonalLimits,
          options: options
        }
      });
    } else {
      showLogoutPopup();
    }

    TrackingUtils.sendAnalytics({
      dataCategory: "User Area Drawer",
      dataAction: "click",
      dataLabel: "Log Out",
      dataValue: ""
    });

    dispatch(
      AccountActionCreators.toggleAccountDrawer(DrawerActionType.UserArea)
    );
    dispatch(MenuActionCreators.select(null));
  };

  const handleClick = () => {
    dispatch(
      AccountActionCreators.toggleAccountDrawer(DrawerActionType.UserArea)
    );
    dispatch(MenuActionCreators.select(null));
  };

  const handleTransactionHistoryClick = () => {
    if (drawerActive) {
      dispatch(AccountActionCreators.toggleAccountDrawer());
    }
    openCashier({
      ActionId: "transactionHistory",
      Area: "User area",
      Element: "Transaction history button",
      Source: "Unified Client",
      TabId: CashierTab.History
    });
  };

  const handleBiometricSettingsClick = () => {
    openBiometricSettings({ cid });
  };

  const UsefulLinksLayout = getLayout(brandId);

  return (
    <React.Fragment>
      {!!UsefulLinksLayout && (
        <UsefulLinksLayout
          handleClick={handleClick}
          handleChatClick={handleChatClick}
          handleOnlineHelpClick={handleOnlineHelp}
          handleHelpCenter={handleHelpCenter}
          handleSendEmailClick={handleContactUs}
          handleTransactionHistoryClick={handleTransactionHistoryClick}
          handleMyAccountOpening={openMyAccount}
          handleBiometricSettingsClick={handleBiometricSettingsClick}
          trackingInfo={trackingInfo}
        />
      )}
      {isMobile && <RegulationLogosSection showControlCenter={false} />}
      <div
        className="logoutButton"
        onClick={handleLogout}
        data-testid={"uc-my-account-area-footer-logout"}
      >
        <span>
          <FormattedMessage {...LocalizationMessages.logoutText} />
        </span>
      </div>
    </React.Fragment>
  );
};

export default React.memo(UsefulLinks);
